/**
 * DisbursementsPage — AP aging detail with vendor criticality, payment timing,
 * and DPO targets.
 */

import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui';
import { useAPAgingPaginated } from './api';
import { formatCurrency } from './utils';
import { cn } from '../../shared/lib/cn';
import { SortableHeader } from './SortableHeader';
import type { VendorCriticality } from './types';

const CRITICALITIES: VendorCriticality[] = ['Red', 'Yellow', 'Green'];

function critBadge(c: VendorCriticality) {
  const colors = {
    Red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    Yellow: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    Green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  };
  return (
    <span className={cn('inline-block px-2 py-0.5 rounded text-xs font-medium', colors[c])}>
      {c}
    </span>
  );
}

export default function DisbursementsPage() {
  const [page, setPage] = useState(1);
  const [criticality, setCriticality] = useState('');
  const [vendor, setVendor] = useState('');
  const [sortBy, setSortBy] = useState('open_balance');
  const [sortDir, setSortDir] = useState('DESC');

  const { data: aging, isLoading } = useAPAgingPaginated({
    page,
    page_size: 25,
    criticality: criticality || undefined,
    vendor: vendor || undefined,
    sort_by: sortBy,
    sort_dir: sortDir,
  });

  const totalPages = aging ? Math.ceil(aging.total / aging.page_size) : 1;

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(d => d === 'DESC' ? 'ASC' : 'DESC');
    } else {
      setSortBy(field);
      setSortDir('ASC');
    }
    setPage(1);
  };

  return (
    <div className="space-y-6 mt-2">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={criticality}
          onChange={e => { setCriticality(e.target.value); setPage(1); }}
          className="text-sm border border-gray-200 dark:border-dark-border rounded-lg px-3 py-1.5 bg-white dark:bg-dark-surface text-charcoal dark:text-gray-200"
        >
          <option value="">All Criticality</option>
          {CRITICALITIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input
          type="text"
          placeholder="Search vendor..."
          value={vendor}
          onChange={e => { setVendor(e.target.value); setPage(1); }}
          className="text-sm border border-gray-200 dark:border-dark-border rounded-lg px-3 py-1.5 bg-white dark:bg-dark-surface text-charcoal dark:text-gray-200 w-48"
        />
        <span className="text-xs text-gray-400 ml-auto">
          {aging ? `${aging.total} records` : ''}
        </span>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2">
                  <SortableHeader label="Vendor" field="vendor_name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Invoice #" field="invoice_number" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Due Date" field="due_date" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Terms" field="payment_terms" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Criticality" field="vendor_criticality" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" />
                  <SortableHeader label="Balance" field="open_balance" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-border/50">
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-gray-400">Loading...</td>
                  </tr>
                ) : !aging?.items.length ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-gray-400">No records found</td>
                  </tr>
                ) : aging.items.map(rec => (
                  <tr key={rec.id} className="hover:bg-gray-50 dark:hover:bg-dark-surface-2/50">
                    <td className="py-2 px-3 text-charcoal dark:text-gray-200">{rec.vendor_name}</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{rec.invoice_number}</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{rec.due_date}</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{rec.payment_terms}</td>
                    <td className="py-2 px-3 text-center">{critBadge(rec.vendor_criticality)}</td>
                    <td className="py-2 px-3 text-right tabular-nums font-medium text-charcoal dark:text-gray-200">
                      {formatCurrency(rec.open_balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-dark-border">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="text-xs px-3 py-1 rounded bg-gray-100 dark:bg-dark-surface-2 disabled:opacity-40">
                Previous
              </button>
              <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="text-xs px-3 py-1 rounded bg-gray-100 dark:bg-dark-surface-2 disabled:opacity-40">
                Next
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
